// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: Apache-2.0

import createClient from "openapi-fetch";

import type { paths } from "@diffclaw/http-api/v1";

export type { components as DiffclawHttpComponents } from "@diffclaw/http-api/v1";

export interface DiffclawClientOptions {
  readonly accessToken?: string;
  readonly baseUrl: string;
  readonly credentials?: RequestInit["credentials"];
  readonly csrfToken?: string;
  readonly fetch?: typeof globalThis.fetch;
  readonly localBootstrapActorId?: string;
}

export interface DiffclawRequestContext {
  readonly actorId: string;
  readonly correlationId: string;
  readonly idempotencyKey: string;
  readonly organizationId: string;
}

export interface DiffclawRequestHeaders {
  readonly "Idempotency-Key": string;
  readonly "X-Correlation-Id": string;
  readonly "X-Diffclaw-Actor-Id": string;
  readonly "X-Diffclaw-Organization-Id": string;
}

export type DiffclawClient = ReturnType<typeof createClient<paths>>;

export const DIFFCLAW_OPERATION_IDS = [
  "acceptGitHubPermissionExpansion",
  "advanceChronicleLearningRun",
  "advanceChronicleBackfill",
  "approveChronicleClaim",
  "approveChronicleConvention",
  "beginHumanLogin",
  "beginReviewPublicationAuthorization",
  "beginProjectDeletion",
  "bootstrapOrganization",
  "cancelChronicleLearningRun",
  "cancelEvaluationRun",
  "cancelReviewRun",
  "changeManagedExecutionPolicy",
  "changeProjectLegalHold",
  "changeProjectPolicy",
  "changeRetention",
  "completeHumanLogin",
  "completeReviewPublicationAuthorization",
  "completeProjectDeletion",
  "connectGitHubRepository",
  "connectRepository",
  "controlChronicleBackfill",
  "correctChronicleClaim",
  "correctPersonalCoachingMemory",
  "createCredentialReference",
  "createProject",
  "createReviewRun",
  "decideEvaluationPromotion",
  "deleteChronicleClaim",
  "deletePersonalCoachingMemory",
  "deprecateReviewProtocol",
  "dispatchReviewConversationProjection",
  "exportChronicle",
  "exportEvidenceArtifact",
  "exportPersonalCoaching",
  "executeReviewRun",
  "finalizeEvaluationRun",
  "getLiveness",
  "getReadiness",
  "generatePersonalCoaching",
  "inspectChronicleBackfill",
  "inspectChronicleCalibration",
  "inspectChronicleLearning",
  "inspectChronicleLearningRuns",
  "inspectChronicleReadiness",
  "inspectPersonalCoaching",
  "inspectCockpitReview",
  "inspectEvaluationPromotion",
  "inspectEvaluationRun",
  "inspectFinding",
  "inspectManagedUsage",
  "inspectProject",
  "inspectProjectLegalHold",
  "inspectProjectPolicy",
  "inspectRepositoryConnection",
  "inspectRetention",
  "inspectReviewChronicleFreshness",
  "inspectReviewConversation",
  "inspectReviewProtocolRelease",
  "inspectReviewRun",
  "inspectReviewSeries",
  "inspectReviewSubjectMaterial",
  "inspectSession",
  "inspectReviewPublicationAuthorization",
  "issueServiceCredential",
  "logoutSession",
  "listCockpitReviews",
  "listEvaluationCorpora",
  "listEvaluationRuns",
  "onboardChronicleCurrentCode",
  "packReviewProtocol",
  "performReviewConversationAction",
  "proposeChronicleClaim",
  "proposeChronicleLearning",
  "proposeFindingDisposition",
  "queryChronicleKnowledge",
  "rebuildChronicleProjection",
  "recordChronicleObservation",
  "recordEvaluationSample",
  "recordPersonalCoachingMemory",
  "recordPrivateReviewNote",
  "recordReviewConversationProviderObservation",
  "registerEvaluationCorpus",
  "registerHumanIdentity",
  "registerReviewSubject",
  "releaseReviewProtocol",
  "repairReviewConversationProjection",
  "requestReviewProtocolPromotion",
  "resolveReviewProtocol",
  "resumeEvaluationRun",
  "resumeReviewRun",
  "revokeCredentialReference",
  "revokeServiceCredential",
  "revokeSession",
  "revokeReviewPublicationAuthorization",
  "runContractDiagnostic",
  "setPersonalCoachingConsent",
  "startChronicleBackfill",
  "startChronicleLearningRun",
  "startEvaluationRun",
  "startReviewRun",
  "supersedeReviewRun",
  "synchronizeChronicleHistorySource",
  "synchronizeReviewConversation",
  "transitionChronicleObservation",
  "transitionChronicleLearning",
  "validateReviewProtocol",
] as const;

export type DiffclawOperationId = (typeof DIFFCLAW_OPERATION_IDS)[number];

export interface DiffclawOperationRequest {
  readonly body?: unknown;
  readonly context?: Partial<DiffclawRequestContext>;
  readonly path?: Readonly<Record<string, string>>;
  readonly query?: Readonly<Record<string, number | string>>;
}

export interface DiffclawArtifactExport {
  readonly bodyBase64: string;
  readonly contentDisposition: string;
  readonly mediaType: string;
  readonly sha256: string;
  readonly sizeBytes: number;
}

interface OperationDescriptor {
  readonly method: "GET" | "POST" | "PUT";
  readonly path: string;
}

export const DIFFCLAW_OPERATIONS: Readonly<
  Record<DiffclawOperationId, OperationDescriptor>
> = {
  acceptGitHubPermissionExpansion: {
    method: "POST",
    path: "/v1/integrations/github/installations/{installationId}/permissions/accept",
  },
  advanceChronicleLearningRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/learning-runs/{runId}/advance",
  },
  advanceChronicleBackfill: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/backfills/{backfillId}/advance",
  },
  approveChronicleClaim: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/claims/{claimId}/approve",
  },
  approveChronicleConvention: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/conventions",
  },
  beginHumanLogin: { method: "POST", path: "/v1/auth/github" },
  beginReviewPublicationAuthorization: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-publication-authorizations",
  },
  completeReviewPublicationAuthorization: {
    method: "POST",
    path: "/v1/auth/github/review-publication/callback",
  },
  inspectReviewPublicationAuthorization: {
    method: "GET",
    path: "/v1/projects/{projectId}/review-publication-authorizations/{authorizationId}",
  },
  revokeReviewPublicationAuthorization: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-publication-authorizations/{authorizationId}/revoke",
  },
  beginProjectDeletion: {
    method: "POST",
    path: "/v1/projects/{projectId}/deletion",
  },
  bootstrapOrganization: {
    method: "POST",
    path: "/v1/bootstrap/organizations",
  },
  cancelChronicleLearningRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/learning-runs/{runId}/cancel",
  },
  cancelEvaluationRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/evaluation/runs/{runId}/cancel",
  },
  cancelReviewRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-runs/{reviewRunId}/cancel",
  },
  changeManagedExecutionPolicy: {
    method: "PUT",
    path: "/v1/projects/{projectId}/managed-execution",
  },
  changeProjectLegalHold: {
    method: "PUT",
    path: "/v1/projects/{projectId}/legal-hold",
  },
  changeProjectPolicy: {
    method: "PUT",
    path: "/v1/projects/{projectId}/policy",
  },
  changeRetention: {
    method: "PUT",
    path: "/v1/projects/{projectId}/retention",
  },
  completeHumanLogin: {
    method: "POST",
    path: "/v1/auth/github/callback",
  },
  completeProjectDeletion: {
    method: "POST",
    path: "/v1/projects/{projectId}/deletion/complete",
  },
  connectGitHubRepository: {
    method: "POST",
    path: "/v1/projects/{projectId}/integrations/github/repositories",
  },
  connectRepository: {
    method: "POST",
    path: "/v1/projects/{projectId}/repository-connections",
  },
  controlChronicleBackfill: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/backfills/{backfillId}/control",
  },
  correctChronicleClaim: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/claims/{claimId}/correct",
  },
  correctPersonalCoachingMemory: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/personal-coaching/memories/{memoryId}/correct",
  },
  createCredentialReference: {
    method: "POST",
    path: "/v1/credential-references",
  },
  createProject: { method: "POST", path: "/v1/projects" },
  createReviewRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-runs",
  },
  decideEvaluationPromotion: {
    method: "POST",
    path: "/v1/projects/{projectId}/evaluation/promotions",
  },
  deleteChronicleClaim: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/claims/{claimId}/delete",
  },
  deletePersonalCoachingMemory: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/personal-coaching/memories/{memoryId}/delete",
  },
  deprecateReviewProtocol: {
    method: "POST",
    path: "/v1/projects/{projectId}/protocols/{protocolId}/releases/{contentDigest}/deprecate",
  },
  dispatchReviewConversationProjection: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-conversations/{conversationId}/provider-intents/{intentId}/dispatch",
  },
  exportChronicle: {
    method: "GET",
    path: "/v1/projects/{projectId}/chronicle/export",
  },
  exportEvidenceArtifact: {
    method: "GET",
    path: "/v1/projects/{projectId}/findings/{findingId}/artifacts/{artifactId}",
  },
  exportPersonalCoaching: {
    method: "GET",
    path: "/v1/projects/{projectId}/chronicle/personal-coaching/export",
  },
  executeReviewRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-runs/{reviewRunId}/execute",
  },
  finalizeEvaluationRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/evaluation/runs/{runId}/finalize",
  },
  getLiveness: { method: "GET", path: "/health/live" },
  getReadiness: { method: "GET", path: "/health/ready" },
  generatePersonalCoaching: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/personal-coaching/generate",
  },
  inspectChronicleBackfill: {
    method: "GET",
    path: "/v1/projects/{projectId}/chronicle/backfills/current",
  },
  inspectChronicleCalibration: {
    method: "GET",
    path: "/v1/projects/{projectId}/chronicle/calibration",
  },
  inspectChronicleLearning: {
    method: "GET",
    path: "/v1/projects/{projectId}/chronicle/learning",
  },
  inspectChronicleLearningRuns: {
    method: "GET",
    path: "/v1/projects/{projectId}/chronicle/learning-runs",
  },
  inspectChronicleReadiness: {
    method: "GET",
    path: "/v1/projects/{projectId}/chronicle/readiness",
  },
  inspectPersonalCoaching: {
    method: "GET",
    path: "/v1/projects/{projectId}/chronicle/personal-coaching",
  },
  inspectCockpitReview: {
    method: "GET",
    path: "/v1/projects/{projectId}/cockpit/reviews/{reviewSeriesId}",
  },
  inspectEvaluationPromotion: {
    method: "GET",
    path: "/v1/projects/{projectId}/evaluation/promotions/{promotionId}",
  },
  inspectEvaluationRun: {
    method: "GET",
    path: "/v1/projects/{projectId}/evaluation/runs/{runId}",
  },
  inspectFinding: {
    method: "GET",
    path: "/v1/projects/{projectId}/findings/{findingId}",
  },
  inspectManagedUsage: {
    method: "GET",
    path: "/v1/projects/{projectId}/managed-execution",
  },
  inspectProject: {
    method: "GET",
    path: "/v1/projects/{projectId}",
  },
  inspectProjectLegalHold: {
    method: "GET",
    path: "/v1/projects/{projectId}/legal-hold",
  },
  inspectProjectPolicy: {
    method: "GET",
    path: "/v1/projects/{projectId}/policy",
  },
  inspectRepositoryConnection: {
    method: "GET",
    path: "/v1/projects/{projectId}/repository-connections/{connectionId}",
  },
  inspectRetention: {
    method: "GET",
    path: "/v1/projects/{projectId}/retention",
  },
  inspectReviewChronicleFreshness: {
    method: "GET",
    path: "/v1/projects/{projectId}/review-runs/{reviewRunId}/chronicle-freshness",
  },
  inspectReviewConversation: {
    method: "GET",
    path: "/v1/projects/{projectId}/review-conversations/{conversationId}",
  },
  inspectReviewProtocolRelease: {
    method: "GET",
    path: "/v1/projects/{projectId}/protocols/{protocolId}/releases/{contentDigest}",
  },
  inspectReviewRun: {
    method: "GET",
    path: "/v1/projects/{projectId}/review-runs/{reviewRunId}",
  },
  inspectReviewSeries: {
    method: "GET",
    path: "/v1/projects/{projectId}/review-series/{reviewSeriesId}",
  },
  inspectReviewSubjectMaterial: {
    method: "GET",
    path: "/v1/projects/{projectId}/review-subjects/{reviewSubjectId}/material",
  },
  inspectSession: { method: "GET", path: "/v1/session" },
  issueServiceCredential: {
    method: "POST",
    path: "/v1/service-credentials",
  },
  logoutSession: { method: "POST", path: "/v1/session/logout" },
  listCockpitReviews: {
    method: "GET",
    path: "/v1/projects/{projectId}/cockpit/reviews",
  },
  listEvaluationCorpora: {
    method: "GET",
    path: "/v1/projects/{projectId}/evaluation/corpora",
  },
  listEvaluationRuns: {
    method: "GET",
    path: "/v1/projects/{projectId}/evaluation/runs",
  },
  onboardChronicleCurrentCode: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/onboarding/current",
  },
  packReviewProtocol: {
    method: "POST",
    path: "/v1/projects/{projectId}/protocols/packages/pack",
  },
  performReviewConversationAction: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-conversations/{conversationId}/actions",
  },
  proposeChronicleClaim: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/claims",
  },
  proposeChronicleLearning: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/learning-proposals",
  },
  proposeFindingDisposition: {
    method: "POST",
    path: "/v1/projects/{projectId}/findings/{findingId}/dispositions",
  },
  queryChronicleKnowledge: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/query",
  },
  rebuildChronicleProjection: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/projection/rebuild",
  },
  recordChronicleObservation: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/observations",
  },
  recordEvaluationSample: {
    method: "POST",
    path: "/v1/projects/{projectId}/evaluation/runs/{runId}/samples",
  },
  recordPersonalCoachingMemory: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/personal-coaching/memories",
  },
  recordPrivateReviewNote: {
    method: "PUT",
    path: "/v1/projects/{projectId}/review-conversations/{conversationId}/private-notes/{noteId}",
  },
  recordReviewConversationProviderObservation: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-conversations/{conversationId}/provider-observations",
  },
  registerEvaluationCorpus: {
    method: "POST",
    path: "/v1/projects/{projectId}/evaluation/corpora",
  },
  registerHumanIdentity: {
    method: "POST",
    path: "/v1/human-identities",
  },
  registerReviewSubject: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-subjects",
  },
  releaseReviewProtocol: {
    method: "POST",
    path: "/v1/projects/{projectId}/protocols/{protocolId}/releases",
  },
  repairReviewConversationProjection: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-conversations/{conversationId}/provider-intents/{intentId}/repair",
  },
  requestReviewProtocolPromotion: {
    method: "POST",
    path: "/v1/projects/{projectId}/protocols/promotions",
  },
  resolveReviewProtocol: {
    method: "POST",
    path: "/v1/projects/{projectId}/protocols/{protocolId}/resolve",
  },
  resumeEvaluationRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/evaluation/runs/{runId}/resume",
  },
  resumeReviewRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-runs/{reviewRunId}/resume",
  },
  revokeCredentialReference: {
    method: "POST",
    path: "/v1/projects/{projectId}/credential-references/{credentialReferenceId}/revoke",
  },
  revokeServiceCredential: {
    method: "POST",
    path: "/v1/service-credentials/{credentialId}/revoke",
  },
  revokeSession: {
    method: "POST",
    path: "/v1/sessions/{sessionId}/revoke",
  },
  runContractDiagnostic: {
    method: "POST",
    path: "/v1/diagnostics/contract",
  },
  setPersonalCoachingConsent: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/personal-coaching/consent",
  },
  startChronicleBackfill: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/backfills",
  },
  startChronicleLearningRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/learning-runs",
  },
  startEvaluationRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/evaluation/runs",
  },
  startReviewRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-runs/start",
  },
  supersedeReviewRun: {
    method: "POST",
    path: "/v1/projects/{projectId}/review-runs/{reviewRunId}/supersede",
  },
  synchronizeChronicleHistorySource: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/backfills/{backfillId}/sources/{sourceId}/synchronize",
  },
  synchronizeReviewConversation: {
    method: "POST",
    path: "/v1/projects/{projectId}/findings/{findingId}/conversation",
  },
  transitionChronicleObservation: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/observations/{observationId}/transition",
  },
  transitionChronicleLearning: {
    method: "POST",
    path: "/v1/projects/{projectId}/chronicle/learning-proposals/{proposalId}/transition",
  },
  validateReviewProtocol: {
    method: "POST",
    path: "/v1/projects/{projectId}/protocols/packages/validate",
  },
};

export class DiffclawHttpError extends Error {
  readonly body: unknown;
  readonly status: number;

  constructor(status: number, body: unknown) {
    super(`Diffclaw request failed with HTTP ${String(status)}`);
    this.name = "DiffclawHttpError";
    this.status = status;
    this.body = body;
  }
}

export function requestContextHeaders(
  context: DiffclawRequestContext,
): DiffclawRequestHeaders {
  return {
    "Idempotency-Key": context.idempotencyKey,
    "X-Correlation-Id": context.correlationId,
    "X-Diffclaw-Actor-Id": context.actorId,
    "X-Diffclaw-Organization-Id": context.organizationId,
  };
}

export function createDiffclawClient(
  options: DiffclawClientOptions,
): DiffclawClient {
  const headers = {
    ...authenticationHeaders(options),
    ...(options.csrfToken === undefined
      ? {}
      : { "X-CSRF-Token": options.csrfToken }),
  };
  return createClient<paths>({
    baseUrl: options.baseUrl,
    credentials: options.credentials ?? "same-origin",
    ...(options.fetch === undefined ? {} : { fetch: options.fetch }),
    ...(Object.keys(headers).length === 0 ? {} : { headers }),
  });
}

function authenticationHeaders(
  options: DiffclawClientOptions,
): Readonly<Record<string, string>> {
  if (
    options.accessToken !== undefined &&
    options.localBootstrapActorId !== undefined
  ) {
    throw new TypeError(
      "Bearer and local-bootstrap authentication cannot be combined",
    );
  }
  if (options.accessToken !== undefined) {
    return { Authorization: `Bearer ${options.accessToken}` };
  }
  if (options.localBootstrapActorId !== undefined) {
    return {
      "X-Diffclaw-Local-Bootstrap-Actor-Id": options.localBootstrapActorId,
    };
  }
  return {};
}

function operationPath(
  template: string,
  values: Readonly<Record<string, string>>,
): string {
  const used = new Set<string>();
  const path = template.replaceAll(
    /\{([A-Za-z][A-Za-z0-9]*)\}/gu,
    (placeholder, name: string) => {
      const value = values[name];
      if (value === undefined || value.length === 0) {
        throw new TypeError(`Missing path parameter ${name}`);
      }
      used.add(name);
      return encodeURIComponent(value);
    },
  );
  const unused = Object.keys(values).filter((name) => !used.has(name));
  if (unused.length > 0) {
    throw new TypeError(`Unexpected path parameter ${unused[0] ?? "unknown"}`);
  }
  return path;
}

function base64(bytes: Uint8Array): string {
  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  let encoded = "";
  for (let index = 0; index < bytes.length; index += 3) {
    const first = bytes[index] ?? 0;
    const second = bytes[index + 1];
    const third = bytes[index + 2];
    encoded += alphabet[first >> 2] ?? "";
    encoded += alphabet[((first & 0b11) << 4) | ((second ?? 0) >> 4)] ?? "";
    encoded +=
      second === undefined
        ? "="
        : (alphabet[((second & 0b1111) << 2) | ((third ?? 0) >> 6)] ?? "");
    encoded += third === undefined ? "=" : (alphabet[third & 0b11_1111] ?? "");
  }
  return encoded;
}

async function artifactExport(
  response: Response,
): Promise<DiffclawArtifactExport> {
  const body = new Uint8Array(await response.arrayBuffer());
  const contentDisposition = response.headers.get("content-disposition");
  const mediaType = response.headers.get("x-diffclaw-artifact-media-type");
  const sha256 = response.headers.get("x-content-sha256");
  const sizeBytes = Number(response.headers.get("content-length"));
  if (
    contentDisposition === null ||
    mediaType === null ||
    sha256 === null ||
    !/^[a-f0-9]{64}$/u.test(sha256) ||
    !Number.isSafeInteger(sizeBytes) ||
    sizeBytes < 1 ||
    sizeBytes !== body.byteLength
  ) {
    throw new TypeError("Evidence artifact response violates its contract");
  }
  return {
    bodyBase64: base64(body),
    contentDisposition,
    mediaType,
    sha256,
    sizeBytes,
  };
}

export async function executeDiffclawOperation(
  options: DiffclawClientOptions,
  operationId: DiffclawOperationId,
  request: DiffclawOperationRequest = {},
): Promise<unknown> {
  const operation = DIFFCLAW_OPERATIONS[operationId];
  const path = operationPath(operation.path, request.path ?? {});
  const url = new URL(path, `${options.baseUrl.replace(/\/+$/u, "")}/`);
  for (const [name, value] of Object.entries(request.query ?? {})) {
    url.searchParams.set(name, String(value));
  }
  const headers = new Headers(authenticationHeaders(options));
  if (options.csrfToken !== undefined) {
    headers.set("X-CSRF-Token", options.csrfToken);
  }
  const context = request.context;
  if (context?.actorId !== undefined) {
    headers.set("X-Diffclaw-Actor-Id", context.actorId);
  }
  if (context?.organizationId !== undefined) {
    headers.set("X-Diffclaw-Organization-Id", context.organizationId);
  }
  if (context?.idempotencyKey !== undefined) {
    headers.set("Idempotency-Key", context.idempotencyKey);
  }
  if (context?.correlationId !== undefined) {
    headers.set("X-Correlation-Id", context.correlationId);
  }
  if (request.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  const fetcher = options.fetch ?? globalThis.fetch;
  const response = await fetcher(url, {
    ...(request.body === undefined
      ? {}
      : { body: JSON.stringify(request.body) }),
    headers,
    method: operation.method,
    credentials: options.credentials ?? "same-origin",
    redirect: "error",
  });
  if (!response.ok) {
    const responseBody: unknown =
      response.status === 204 ? undefined : await response.json();
    throw new DiffclawHttpError(response.status, responseBody);
  }
  return operationId === "exportEvidenceArtifact"
    ? artifactExport(response)
    : response.status === 204
      ? undefined
      : response.json();
}
